/**
 * Is this phone fit to be taken into a switchroom?
 *
 * The failure this exists to prevent: an engineer works a full shift with no
 * signal, photographs several hundred shots, comes back out, and finds nothing
 * was kept. The test has to be redone — and on a live board that is not an
 * inconvenience, it is another energised access, another permit, another day.
 *
 * Every way that happens is knowable *before* they walk in:
 *
 *  - the register, script and shot list were never cached, so there is nothing
 *    to capture against;
 *  - the sign-in will lapse during the shift, and a single-page app's refresh
 *    token lasts 24 hours, so it cannot renew itself in a basement;
 *  - the browser never promised to keep the data, so the OS may evict it — on
 *    iOS, automatically after seven days for an app that was not installed;
 *  - there is not enough room left for the photos the job needs;
 *  - work from the last shift is still sitting unsynced on the phone.
 *
 * So the check runs against facts gathered while there is still signal, and it
 * is allowed to say **no**. A readiness screen that always goes green is
 * decoration.
 *
 * This module is pure. The facts are gathered elsewhere; here they are judged,
 * so the judgement can be tested without a device.
 */

import type { Platform } from '../platform/ios.js';

export type Severity = 'stop' | 'warn' | 'ok';

export interface Finding {
  id: string;
  severity: Severity;
  title: string;
  /** What is true, in the engineer's terms. */
  detail: string;
  /** What to do about it. Absent when there is nothing to do. */
  fix?: string | undefined;
}

export interface ReadinessFacts {
  /** When the SharePoint token runs out. Null when there is no usable token. */
  tokenExpiresAt: number | null;
  /** Whether each thing needed to work offline is in IndexedDB. */
  cached: { register: boolean; script: boolean; profiles: boolean };
  /** When that cache was last refreshed. Null when never. */
  cachedAt: number | null;
  storage: {
    persisted: boolean;
    /** Null when the browser will not say. Unknown is not the same as fine. */
    headroomPhotos: number | null;
    supported: boolean;
  };
  platform: Platform;
  /** Photos this shift still has to take. */
  plannedPhotos: number;
  /** Captures from earlier that have not reached the server. */
  pendingUploads: number;
  online: boolean;
  now: number;
}

export interface Readiness {
  verdict: Severity;
  findings: Finding[];
  /** One line for the top of the screen. */
  summary: string;
}

const HOUR = 60 * 60 * 1000;
/** A long switchroom day. The token has to outlast it. */
export const SHIFT_MS = 10 * HOUR;
/** A register older than this is probably not the board in front of them. */
const STALE_CACHE_MS = 7 * 24 * HOUR;

const hours = (ms: number): string => {
  const h = ms / HOUR;
  if (h < 1) return `${Math.max(1, Math.round(ms / (60 * 1000)))} minutes`;
  return `${h < 10 ? h.toFixed(1) : Math.round(h)} hours`;
};

function tokenFinding(f: ReadinessFacts): Finding {
  if (f.tokenExpiresAt === null) {
    return {
      id: 'token',
      severity: 'stop',
      title: 'Not signed in',
      detail: 'There is no valid sign-in, so nothing can be uploaded and the register cannot be refreshed.',
      fix: 'Sign in now, while you have signal.',
    };
  }
  const left = f.tokenExpiresAt - f.now;
  if (left <= 0) {
    return {
      id: 'token',
      severity: 'stop',
      title: 'Sign-in has expired',
      detail: 'The session ran out. It cannot renew itself without signal, so captures would have nowhere to go.',
      fix: 'Sign in again before you lose signal.',
    };
  }
  if (left < SHIFT_MS) {
    return {
      id: 'token',
      severity: 'warn',
      title: `Sign-in lapses in ${hours(left)}`,
      detail:
        'It will run out before a full shift is over. Captures are still kept on the phone, but they cannot upload until you sign in again.',
      fix: 'Sign in again now so the session covers the whole shift.',
    };
  }
  return {
    id: 'token',
    severity: 'ok',
    title: `Signed in, ${hours(left)} left`,
    detail: 'The session covers a full shift.',
  };
}

function cacheFinding(f: ReadinessFacts): Finding {
  const missing = [
    !f.cached.register && 'the register',
    !f.cached.script && 'the test script',
    !f.cached.profiles && 'the shot list',
  ].filter(Boolean) as string[];

  if (missing.length) {
    return {
      id: 'cache',
      severity: 'stop',
      title: 'Not downloaded for offline use',
      detail: `This phone does not hold ${missing.join(' or ')}. With no signal there would be nothing to capture against.`,
      fix: 'Open the boards and the script once while you still have signal.',
    };
  }
  if (f.cachedAt !== null && f.now - f.cachedAt > STALE_CACHE_MS) {
    return {
      id: 'cache',
      severity: 'warn',
      title: 'Register is more than a week old',
      detail: 'Breakers may have been added or corrected since this copy was taken.',
      fix: 'Refresh it before you go in.',
    };
  }
  return { id: 'cache', severity: 'ok', title: 'Downloaded for offline use', detail: 'Register, script and shot list are on this phone.' };
}

function persistenceFinding(f: ReadinessFacts): Finding {
  if (f.storage.persisted) {
    return {
      id: 'persistence',
      severity: 'ok',
      title: 'Storage is protected',
      detail: 'The browser has promised not to clear this app’s data on its own.',
    };
  }
  // iOS evicts a non-installed web app's storage after about seven days of no
  // use, and under pressure sooner. Installing is what buys the promise.
  if (f.platform.ios && !f.platform.installed) {
    return {
      id: 'persistence',
      severity: 'stop',
      title: 'Not installed — photos can be deleted by iOS',
      detail:
        'Run from Safari rather than the Home Screen, this app’s storage is temporary. iOS can clear it without warning, and does so by itself after about a week.',
      fix: 'Share → Add to Home Screen, then open it from there.',
    };
  }
  return {
    id: 'persistence',
    severity: 'warn',
    title: 'Storage is not protected',
    detail: f.storage.supported
      ? 'The browser would not promise to keep this app’s data, so it may be cleared if the device runs short of space.'
      : 'This browser will not say whether it keeps app data. Treat anything held on the phone as temporary.',
    fix: 'Install the app and sync often.',
  };
}

function roomFinding(f: ReadinessFacts): Finding {
  const { headroomPhotos } = f.storage;
  if (headroomPhotos === null) {
    return {
      id: 'room',
      severity: 'warn',
      title: 'Free space unknown',
      detail: 'This browser will not say how much room is left, so the phone could fill up mid-shift without warning.',
      fix: 'Sync during the shift rather than at the end of it.',
    };
  }
  if (headroomPhotos < f.plannedPhotos) {
    return {
      id: 'room',
      severity: 'stop',
      title: `Room for ${headroomPhotos} photos, ${f.plannedPhotos} to take`,
      detail: 'The phone will fill before the job is done, and captures after that point would be lost.',
      fix: 'Sync and free space now, or split the work across two visits.',
    };
  }
  if (headroomPhotos < f.plannedPhotos * 1.5) {
    return {
      id: 'room',
      severity: 'warn',
      title: `Room for ${headroomPhotos} photos, ${f.plannedPhotos} to take`,
      detail: 'Enough, but with little to spare. A retake or two could run it close.',
      fix: 'Sync partway through the shift.',
    };
  }
  return {
    id: 'room',
    severity: 'ok',
    title: `Room for about ${headroomPhotos} photos`,
    detail: `Comfortably more than the ${f.plannedPhotos} this job needs.`,
  };
}

function backlogFinding(f: ReadinessFacts): Finding | null {
  if (f.pendingUploads === 0) return null;
  // Carrying yesterday's work into a basement means one eviction loses two days.
  return {
    id: 'backlog',
    severity: f.online ? 'warn' : 'stop',
    title: `${f.pendingUploads} capture${f.pendingUploads === 1 ? '' : 's'} not yet uploaded`,
    detail: f.online
      ? 'Work from earlier is still only on this phone. Taking it underground doubles what one wipe would cost.'
      : 'Work from earlier is still only on this phone and there is no signal to send it.',
    fix: f.online ? 'Let it finish uploading before you go in.' : 'Find signal and let it upload before going further.',
  };
}

const RANK: Record<Severity, number> = { ok: 0, warn: 1, stop: 2 };

export function assess(f: ReadinessFacts): Readiness {
  const findings = [
    tokenFinding(f),
    cacheFinding(f),
    persistenceFinding(f),
    roomFinding(f),
    backlogFinding(f),
  ].filter((x): x is Finding => x !== null);

  findings.sort((a, b) => RANK[b.severity] - RANK[a.severity]);
  const verdict = findings.reduce<Severity>((worst, x) => (RANK[x.severity] > RANK[worst] ? x.severity : worst), 'ok');

  const stops = findings.filter((x) => x.severity === 'stop').length;
  const warns = findings.filter((x) => x.severity === 'warn').length;

  const summary =
    verdict === 'stop'
      ? `Not ready — ${stops} thing${stops === 1 ? '' : 's'} would lose work offline.`
      : verdict === 'warn'
        ? `Usable, with ${warns} thing${warns === 1 ? '' : 's'} to watch.`
        : 'Ready to go offline.';

  return { verdict, findings, summary };
}

/**
 * The other end of the shift: is it safe to put the phone away?
 *
 * The quiet disaster is an engineer who finishes, assumes the app sent
 * everything, and clears it or lets the phone sit until iOS reclaims the space.
 */
export function safeToLeave(pendingUploads: number, online: boolean): Finding {
  if (pendingUploads === 0) {
    return { id: 'leave', severity: 'ok', title: 'Everything is uploaded', detail: 'Nothing is left on this phone.' };
  }
  return {
    id: 'leave',
    severity: 'stop',
    title: `${pendingUploads} capture${pendingUploads === 1 ? '' : 's'} still on this phone`,
    detail: online
      ? 'The upload has not finished. Closing the app now is fine; clearing it, or leaving it unopened for a week, is not.'
      : 'There is no signal. These exist nowhere else.',
    fix: online ? 'Stay here until the count reaches zero.' : 'Go where there is signal and open the app until the count reaches zero.',
  };
}
